import type { EndpointDTO, WorkloadStateDTO } from '@devrobot/shared';
import { OBSERVATION_STALE_SECONDS } from '@devrobot/shared';
import { CircleCheck, CircleDashed, CircleMinus, EyeOff, History } from 'lucide-react';
import { RelativeTime } from './RelativeTime';
import { StatusBadge } from './StatusBadge';

function endpointSummary(state: WorkloadStateDTO, endpoints?: EndpointDTO[]): { label: string; tone: 'success' | 'warning' | 'muted' } {
  if (endpoints && endpoints.length === 0) return { label: 'none exposed', tone: 'muted' };
  if (state.endpointsReady) return { label: 'ready', tone: 'success' };
  if (!endpoints) return { label: 'not ready', tone: 'warning' };
  const ready = endpoints.filter((e) => e.dns === 'ok' && (e.kind === 'port' || e.certificate === 'valid')).length;
  return { label: `${ready}/${endpoints.length} ready`, tone: 'warning' };
}

const TONE_BADGE = { success: 'badge badge-soft badge-success gap-1', warning: 'badge badge-soft badge-warning gap-1', muted: 'badge badge-ghost gap-1' } as const;
const TONE_ICON = { success: CircleCheck, warning: CircleDashed, muted: CircleMinus } as const;

/**
 * The separate state dimensions of a deployment target (FR-OPS-01): process, health, endpoint
 * readiness and last observation. Stale values are labelled as last known; "unknown" is never
 * shown as stopped.
 */
export function StateDimensions({ state, endpoints, compact = false }: { state: WorkloadStateDTO; endpoints?: EndpointDTO[]; compact?: boolean }) {
  const stale = state.stale;
  const unobservable = !stale && (state.process === 'unknown' || !state.observedAt);
  const ep = endpointSummary(state, endpoints);
  const EpIcon = TONE_ICON[ep.tone];

  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <StatusBadge kind="process" value={state.process} stale={stale} prefix="process" size="xs" />
        <StatusBadge kind="health" value={state.health} stale={stale} prefix="health" size="xs" />
        <span className={`${TONE_BADGE[ep.tone]} badge-xs whitespace-nowrap`}>
          <span className="opacity-70">endpoints</span> {ep.label}
        </span>
        <span className="text-base-content/60">
          observed <RelativeTime value={state.observedAt} />
        </span>
      </div>
    );
  }

  const cells = [
    { label: 'Process', value: <StatusBadge kind="process" value={state.process} stale={stale} /> },
    { label: 'Health', value: <StatusBadge kind="health" value={state.health} stale={stale} /> },
    { label: 'Endpoints', value: <span className={`${TONE_BADGE[ep.tone]} badge-sm whitespace-nowrap`}>
          <EpIcon className="size-3" aria-hidden="true" />
          {ep.label}
        </span> },
    {
      label: 'Last observed',
      value: (
        <span className="inline-flex flex-wrap items-center gap-1.5 text-sm">
          <RelativeTime value={state.observedAt} />
          {stale ? (
            <span className="badge badge-soft badge-warning badge-sm gap-1">
              <History className="size-3" aria-hidden="true" /> stale
            </span>
          ) : null}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {cells.map((c) => (
          <div key={c.label} className="min-w-0 rounded-field bg-base-200/60 p-3">
            <div className="mb-1.5 text-xs text-base-content/55">{c.label}</div>
            {c.value}
          </div>
        ))}
      </div>
      {stale ? (
        <div role="status" className="alert alert-warning alert-soft text-sm">
          <History className="size-4 shrink-0" aria-hidden="true" />
          <span>
            The last observation is older than {OBSERVATION_STALE_SECONDS} seconds. These are the last known values, not live confirmation.
          </span>
        </div>
      ) : null}
      {unobservable ? (
        <div role="status" className="alert alert-info alert-soft text-sm">
          <EyeOff className="size-4 shrink-0" aria-hidden="true" />
          <span>DevRobot cannot observe this target right now. "Unknown" does not mean stopped: the application may still be running.</span>
        </div>
      ) : null}
    </div>
  );
}
